import { useCallback, useEffect, useRef, useState } from 'react';
import { viewKey, aboutThisRoom, movesTheReader, whoMovedTheView, willChangeTheView, MicrophoneDidNotOpenError, NotAHostError, QUEUE_ROWS, RefusedError, SocialAuthError, doorControls, mayRunRoom, rosterFor, stageSeats } from '@versesverse/core';
import type { FollowView, BannedReaderView, DoorControl, HallView, RoomAccess, RoomView } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import type { Strings } from '../i18n/strings';
import { RoomChat } from './RoomChat';
import type { RoomConnection, RoomDeparture, RoomParticipant, VoiceSession } from './voiceSession';
import { refusalSays } from '../i18n/refusal';
import { useOneAtATime } from './useOneAtATime';

/** The host controls, as this component needs them. The social client satisfies it. */
export interface RoomModeration {
  muteParticipant(roomId: string, userId: string): Promise<void>;
  setOnStage(roomId: string, userId: string, onStage: boolean): Promise<RoomView>;
  removeParticipant(roomId: string, userId: string): Promise<void>;
  banFromRoom(roomId: string, userId: string): Promise<RoomView>;
  /** Change who may come in, and mint a fresh secret by doing it (D24) — hosts only. */
  setRoomAccess(roomId: string, access: RoomAccess, passphrase?: string): Promise<RoomView>;
  /** Whom this room has barred — its own leadership only, and the read `unbanFromRoom` needed. */
  roomBanned(roomId: string): Promise<BannedReaderView[]>;
  /** Let somebody back in. Reachable at last, because there is now a way to name them. */
  unbanFromRoom(roomId: string, userId: string): Promise<RoomView>;
  /** Take every microphone but the hosts' — the control a session needs (§6). */
  silenceRoom(roomId: string): Promise<RoomView>;
  /** Ask to speak. Not a host action — the only room control that is every reader's (D22). */
  raiseHand(roomId: string): Promise<RoomView>;
  /** Put a hand down: your own always, somebody else's only as a host. */
  lowerHand(roomId: string, userId: string): Promise<RoomView>;
  /** Look a Hall up by the address a leader publishes — how one community names another. */
  hall(slug: string): Promise<HallView | null>;
  /** Ask a second Hall to co-host this room. Attaches nothing until they accept (D22). */
  proposeCoHost(roomId: string, hallId: string): Promise<RoomView>;
  /** Withdraw the invitation, or part ways — either leadership may. */
  endCoHost(roomId: string): Promise<RoomView>;
  /** Share hostship — a room outside a Hall has one moderator and may hold three hundred. */
  addHost(roomId: string, userId: string): Promise<RoomView>;
  closeRoom(roomId: string): Promise<RoomView>;
  /**
   * READ THIS ROOM AGAIN — the only correct answer to somebody saying it changed.
   *
   * Asked for only when the room has actually moved: see the effect below. It carries the session,
   * though the route is public — a host's answer includes the invite code and an anonymous read
   * would hand this panel a poorer room than the one it already has, which is how the code came to
   * vanish off a host's screen.
   */
  room(roomId: string): Promise<RoomView | null>;
}

export interface ActiveRoomProps {
  client: RoomModeration;
  room: RoomView;
  session: VoiceSession;
  meId: string | null;
  /** Whether the SERVER will honour a moderation call from this reader — see the note below. */
  mayModerate?: boolean;
  /** What to call this room — its own title, or the node's name when nobody named it (D10). */
  title: string;
  /** Leaving. Carries WHY when the reader did not choose it — see `RoomDeparture`. */
  onLeave(why?: RoomDeparture): void;
  onRoomChanged(room: RoomView): void;
  /**
   * TAKE THIS READER WHERE THE ROOM IS LOOKING — §11.
   *
   * The panel does not know what a view IS: it checks who may drive and hands the move up, and
   * the app opens it with the same function that opens an arriving link. Absent for a caller that
   * has no map to move — the phone today — and then nothing subscribes at all.
   */
  onFollowed?(view: FollowView): void;
  /**
   * WHAT THIS READER IS LOOKING AT — handed down so the room can send it, and so it can tell a
   * move this reader made from one it applied for somebody else.
   *
   * The app owns the view; this panel never invents one. Absent for a caller with no map.
   */
  view?: FollowView;
}

/**
 * How each door control is drawn — its words and the class the browser gate looks for.
 *
 * A total map over `DoorControl['kind']`: core decides WHICH controls a room's door has, this file
 * decides what they look like, and a kind added there stops this compiling until somebody says what
 * it is called. The class names are unchanged from when this markup was three hand-written buttons,
 * because the specs that press them are the proof this control is reachable at all.
 */
const DOOR_BUTTONS: Record<DoorControl['kind'], { className: string; label: keyof Strings }> = {
  lock: { className: 'room-lock', label: 'rooms.lockDoor' },
  open: { className: 'room-unlock', label: 'rooms.openDoor' },
  rotate: { className: 'room-newcode', label: 'rooms.newCode' },
};

/*
 * THE ROOM YOU ARE IN (COMMUNICATION.md §6).
 *
 * Who is here, whether they are speaking, your own microphone — and, if you host it, the controls
 * over everyone else. Every control is a call to OUR server, which checks that you host this room
 * before it touches the SFU (D18): nothing here is authorised by the token the browser holds, so a
 * reader who edits this component out of the page gains exactly nothing.
 *
 * Host controls are rendered only for a host, which is a courtesy to the UI and not the security —
 * the server refuses them either way, and a NotAHostError is shown rather than swallowed, because
 * hostship can be revoked while the panel is open.
 */
export function ActiveRoom({ client, room, session, meId, title, mayModerate, onLeave, onRoomChanged, onFollowed, view }: ActiveRoomProps): React.JSX.Element {
  const { t } = useLang();
  const [participants, setParticipants] = useState<RoomParticipant[]>([]);

  /*
   * THIS READER, OFF THE ROSTER — the one row that is about us, read once and used for both of the
   * things the server decides about a reader in a room.
   *
   * The microphone reads `muted` from it (see the paragraph over `micOn`), and DRIVING reads
   * `canSpeak`: moving three hundred readers to another verse is a capability and not a right
   * (§11), it belongs to hosts and speakers, and that is exactly the grant `capabilitiesFor`
   * minted. Reading both off the same row is what stops this panel inventing a second opinion
   * about what the server allowed.
   */
  const mine = participants.find(participant => participant.identity === meId);
  const iMayDrive = mine?.canSpeak === true;

  /*
   * WHERE THIS READER IS LOOKING, PUT ON THE WIRE — the one place that does it, and the one place
   * that decides what a failure to means.
   *
   * `publishData` rejects while the data channel is down, which is exactly when a leader is most
   * likely to be moving: they cannot hear anybody, so they read on. Nothing was catching it, so a
   * lost move was an unhandled rejection AND a room that silently did not follow.
   *
   * There is no sentence to say. A leader is already told the connection is going, and a second
   * line about a passage would not tell them anything they can act on. What is owed is the MOVE,
   * and the answer to a lost one is to send where they are looking when the connection returns —
   * which is the honest one, because a room follows a leader to where they ARE and not to the
   * verse they happened to be on when the lift doors closed.
   */
  const sendWhereIAmLooking = useCallback((showing: FollowView | undefined): void => {
    /* A driver who has left the passage is leaving, not teaching — `aboutThisRoom` carries why. */
    if (!aboutThisRoom(showing, room.nodeId)) return;
    session.sendFollow?.(showing ?? {})?.catch(() => {});
  }, [session, room.nodeId]);
  const [refused, setRefused] = useState(false);
  /** What the server said, when it explained rather than merely refused. */
  const [said, setSaid] = useState<string | null>(null);
  const [connection, setConnection] = useState<RoomConnection>('live');
  /**
   * Who this reader has turned down, for themselves.
   *
   * Deliberately NOT persisted and deliberately not sent anywhere. It lasts as long as the room
   * does, which is as long as the reason for it usually does — and a stored list of people you
   * cannot bear to hear is a thing this product should not be keeping about anybody.
   */
  const [hushed, setHushed] = useState<string[]>([]);
  /** The address of a community being asked to co-host — how one leader names another. */
  const [coHostSlug, setCoHostSlug] = useState('');
  /** Whether this reader has opened the whole roster of a room too big to draw at once. */
  const [wholeRoom, setWholeRoom] = useState(false);
  /** Whom this room has barred — asked for only by somebody who may lift it. */
  const [barred, setBarred] = useState<BannedReaderView[]>([]);
  /*
   * WHETHER THIS BROWSER IS PLAYING THE ROOM AT ALL — see `onPlaybackBlocked` on the seam.
   *
   * Not our failure and entirely the reader's problem: everything looks right and they hear
   * nothing. It is drawn first, above the room, because there is no point reading the rest of a
   * conversation you cannot hear.
   */
  const [deaf, setDeaf] = useState(false);
  /*
   * WHETHER THIS READER IS FOLLOWING THE ROOM — on by default, off the moment they say so.
   *
   * §11 is explicit that following is **opt-out, per reader**: being dragged around a text you are
   * reading is hostile, and it is worst for somebody who joined late and is still finding their
   * place. The default on entering is to follow, because that is why you came.
   */
  /*
   * THE MICROPHONE WENT UP AND CARRIED NOTHING — see `onSilentMicrophone` on the seam.
   *
   * Kept until the reader presses the button again, because the SDK looks once, at publish: there
   * is no second event saying sound has started, and a line that cleared itself on a timer would
   * be guessing. Pressing is the honest way out — it republishes and looks again.
   */
  const [micSilent, setMicSilent] = useState(false);
  const [following, setFollowing] = useState(true);
  /* Whether that read FAILED, which is not the same answer as nobody being barred — see below. */
  const [barredUnread, setBarredUnread] = useState(false);

  useEffect(() => session.onParticipants(setParticipants), [session]);

  /*
   * WHEN SOMEBODY SAYS THE ROOM CHANGED, ASK THE SERVER WHAT IT IS NOW.
   *
   * A reader's copy of the room is a snapshot from the moment they joined — nothing re-read it — so
   * every setting a LEADER changed was invisible to everyone else. The grant was never the problem:
   * that lives on the SFU and arrives by itself. The ROOM did. Silencing takes the microphones and
   * the listeners were then offered **no way to ask for one back**, because "raise a hand" is drawn
   * from the room's policy and their copy still said open; from the other side a hand went up and
   * the queue on the leader's screen never moved. Found by two browsers in one room, and by nothing
   * else — one browser plus a fake connector has nobody on the other side to change anything.
   *
   * The nudge carries no state (`core/room-wire.ts`), so this asks the server rather than
   * believing a peer. A forged one costs one read and can say nothing false.
   *
   * The callback lives in a ref because `Rooms` passes a fresh arrow every render, and depending on
   * it would re-subscribe forever.
   */
  const changed = useRef(onRoomChanged);
  changed.current = onRoomChanged;

  /*
   * WHERE THE ROOM IS LOOKING — applied only when this reader is following, and only from somebody
   * the ROSTER says may drive.
   *
   * A packet cannot carry authority: anybody in the room can publish one, so the check is made
   * here against the list this panel is already drawing. `canSpeak` is the capability the server
   * minted (§5.2 `capabilitiesFor`), which is exactly §11's rule — hosts and speakers — arriving
   * by the path that already keeps the two ends from disagreeing. A sender who is not in the
   * roster at all is not refused as a stranger: they are simply somebody this browser has not been
   * told about yet, and applying their move would be trusting the packet.
   *
   * The listener lives in a ref because the app hands a fresh callback every render, and depending
   * on it would re-subscribe forever — the same reason `onRoomChanged` does.
   */
  const followed = useRef(onFollowed);
  followed.current = onFollowed;

  /*
   * WHAT THIS READER IS LOOKING AT, and which of three things a change to it means.
   *
   * The view arrives as one prop and changes for two quite different reasons: this reader moved,
   * or a move was applied on somebody else's behalf. Telling them apart is what makes both halves
   * of §11 work — the leader's move goes out, and a FOLLOWER who navigates away stops following
   * without pressing anything, which is the piece §11 asks for and the control alone cannot give.
   *
   * `applied` holds the key of a move this panel has just handed up, and only when that move will
   * actually change the view — a leader pointing where this reader already is changes nothing, and
   * a suppression waiting for a change that never comes would swallow the reader's next move.
   *
   * The keys come from core, so the app's object and the one off the wire compare equal whatever
   * order each was built in.
   */
  const seen = useRef(viewKey(view ?? {}));
  const applied = useRef<string | null>(null);

  useEffect(() => {
    const now = viewKey(view ?? {});
    const moved = whoMovedTheView(now, seen.current, applied.current);
    if (moved === 'nobody') return;
    seen.current = now;

    /* The first change after a move we applied is that move landing — or the app landing somewhere
       near it, when a part did not resolve. Either way it is not the reader's doing. */
    if (moved === 'the room') { applied.current = null; return; }

    /*
     * A DRIVER WHO HAS LEFT THE PASSAGE IS LEAVING, NOT TEACHING — measured, not reasoned.
     *
     * A live gate asserted the other reading first: the leader walks to another passage, the
     * follower is told the room moved somewhere they cannot follow. It went red, and the reason is
     * the one the gate was written to expose — walking away unmounts the room's panel, which
     * disconnects, and the publish loses the race with its own transport closing. The follower
     * learns nothing.
     *
     * The right answer was in that: there is no move to send. A leader who walks to another
     * passage has left the room, and the ROSTER already says so — a sentence about a room that
     * "moved" would describe something that did not happen.
     */
    if (iMayDrive) {
      sendWhereIAmLooking(view);
      return;
    }

    /* A reader who walked off on their own has stopped following, and the control says so — which
       is also how they re-join. §11: they are not snapped back. */
    setFollowing(false);
  }, [view, iMayDrive, sendWhereIAmLooking]);

  useEffect(() => {
    /*
     * BOTH HALVES OR NEITHER. Without the app's view this panel cannot tell a move within the
     * passage from one that would cost the reader the room, and guessing either way is a defect:
     * hold everything and the feature is inert, apply everything and a follower is dropped.
     */
    if (!session.onFollow || !onFollowed || !view) return undefined;
    return session.onFollow((from, moved) => {
      /*
       * THE THREE REFUSALS ARE `movesTheReader`, IN CORE — is this reader still following, does the
       * roster say this sender may drive, and is the move about this room at all. The last is the
       * defence against a packet that should not exist: without it a forged move would drag every
       * follower out of the room. All three are dropped without a word, deliberately.
       *
       * They are stated there rather than here because the phone will ask exactly the same three,
       * and "who may drive" is not a rule this layer can afford in two copies.
       */
      if (!movesTheReader({ following, roster: participants, from, moved, roomNodeId: room.nodeId })) return;
      /* Recorded only when it will change something — see the effect above. */
      if (willChangeTheView(moved, seen.current)) applied.current = viewKey(moved);
      followed.current?.(moved);
    });
    /* `participants` is in the deps on purpose: a listener closing over an old roster would go on
       refusing somebody who has since been given the floor. */
  }, [session, onFollowed, following, participants, view]);

  useEffect(() => {
    if (!session.onSilentMicrophone) return undefined;
    return session.onSilentMicrophone(() => setMicSilent(true));
  }, [session]);

  useEffect(() => {
    if (!session.onPlaybackBlocked) return undefined;
    /* The seam pushes the CURRENT answer on subscribe, so a reader who arrives already blocked is
       told without waiting for a change that has already happened. */
    return session.onPlaybackBlocked(setDeaf);
  }, [session]);

  /*
   * WHAT THE ROOM IS NOW — asked of the SERVER, by both of the things that make our copy stale.
   *
   * The nudge below is a HINT, and a hint is a thing that can be missed. It rides the data
   * channel, and a data channel is per-connection: nothing replays what was published while this
   * reader was reconnecting. So the fix that stopped a room being a snapshot of the moment a
   * reader joined left a second snapshot behind it — a copy frozen at the last nudge they were
   * connected for — and it is the same harm, word for word, arriving through the one door where
   * hints are guaranteed to be lost.
   *
   * One function rather than two reads: the answer to "what is the room now" must not differ
   * between the two callers, and this repository has that scar twice already.
   */
  const askWhatTheRoomIsNow = useCallback((stillHere: () => boolean): void => {
    void client.room(room.id)
      .then(fresh => { if (stillHere() && fresh) changed.current(fresh); })
      .catch(() => {});
  }, [client, room.id]);

  useEffect(() => {
    if (!session.onChangeHint) return;
    /*
     * `live` WAS NEVER SET FALSE — a guard that reads like one and could not fire.
     *
     * The effect returned the unsubscribe directly, so nothing closed over the flag: it was true
     * when the room mounted and true for ever after. A nudge landing just before a reader leaves
     * therefore still resolved into `changed.current`, handing the panel above a room the reader
     * is no longer in — and the line `if (live && fresh)` said in review that this had been
     * thought about.
     *
     * The cleanup now does both, which is what it looked like it was doing.
     */
    let live = true;
    const stop = session.onChangeHint(() => askWhatTheRoomIsNow(() => live));
    return () => { live = false; stop(); };
  }, [session, askWhatTheRoomIsNow]);

  /*
   * RE-APPLY THE HUSH WHENEVER THE ROSTER MOVES — and this is a defect in the control as first
   * written, found the day after.
   *
   * `setVolume` is set on the RemoteParticipant object that exists at the moment it is called.
   * Somebody who leaves and comes back — a dropped connection, a walk out and in, a phone changing
   * cell — arrives as a NEW object at full volume, while `hushed` still lists them. The button
   * would read "Hear them again" about somebody the reader could already hear: the UI claiming a
   * state the audio does not have, which is worse than not offering the control at all.
   *
   * Keyed on `participants` because that is what changes when somebody arrives. Setting a volume
   * changes no state here, so there is no loop, and re-applying to a participant already at zero
   * costs nothing.
   */
  useEffect(() => {
    if (!session.setParticipantMuted) return;
    for (const identity of hushed) session.setParticipantMuted(identity, true);
  }, [participants, hushed, session]);

  /*
   * OUR OWN CONNECTION — the participant the roster above cannot report on.
   *
   * `reconnecting` is shown and nothing else happens: the SDK is trying, a gap on a train is not a
   * reason to throw somebody out of a conversation, and the line answers the question they actually
   * have, which is "why has nobody said anything".
   *
   * `dropped` LEAVES. The SDK has finished trying and the SFU has already told our server the
   * participant went, so the room on screen no longer exists for anybody else — keeping it drawn
   * would leave a reader talking into a panel with a mute button that mutes nothing. Leaving puts
   * them back at the room list, where the room is still listed and one tap rejoins it.
   */
  /*
   * A GAP IS THE ONE PLACE A NUDGE CANNOT REACH, so coming back out of one asks.
   *
   * `reconnecting` means the data channel is down; the server announces a room change over that
   * channel and nothing queues it. A leader who silences the room, locks the door or hands
   * somebody the floor while a reader is in a lift is therefore invisible to that reader FOR
   * EVER — the microphones go, because the grant is the SFU's and arrives by itself, and the
   * panel goes on offering a hand-raise the room no longer takes and hiding one it now does.
   *
   * Only after a gap. `onConnection` says `live` the moment it is subscribed to — that is how a
   * reader who arrives already connected is told anything — and reading the room again there
   * would be a second request for the room this panel was just handed, on every mount.
   */
  const wasInAGap = useRef(false);
  /* What to send when it closes. In a ref because the connection is subscribed to once and a
     dependency on the view would tear the subscription down on every step across the map. */
  const looking = useRef({ view, iMayDrive });
  looking.current = { view, iMayDrive };

  useEffect(() => {
    if (!session.onConnection) return undefined;
    let live = true;
    const stop = session.onConnection((next, why) => {
      setConnection(next);
      if (next === 'reconnecting') wasInAGap.current = true;
      if (next === 'live' && wasInAGap.current) {
        wasInAGap.current = false;
        askWhatTheRoomIsNow(() => live);
        /* And the room catches up with a leader who read on while they were gone. Every move made
           during the gap was published into a closed channel; only the last one still matters. */
        if (looking.current.iMayDrive) sendWhereIAmLooking(looking.current.view);
      }
      /* The reason travels UP, because this component is about to unmount and the sentence has to
         be said by whatever is still on screen — see `RoomDeparture` for why four reasons and not
         one, and why "connection lost" is false for three of them. */
      if (next === 'dropped') onLeave(why ?? 'lost');
    });
    return () => { live = false; stop(); };
  }, [session, onLeave, askWhatTheRoomIsNow, sendWhereIAmLooking]);

  /*
   * WHETHER TO OFFER THE CONTROLS — the server's answer, not our guess.
   *
   * This was `room.hostIds.includes(meId)`, and the server's rule is wider: a Hall's owner and
   * co-hosts moderate that Hall's rooms without hosting each one (D22), and a site moderator
   * moderates anywhere. So a leader would appoint the mod team D22 calls the point of a Hall, the
   * co-host would walk into the room, and the product would show them nothing — while the server
   * stood ready to honour every action they could not reach.
   *
   * `mayModerate` comes back with the join token. Falling back to `hostIds` keeps an older client
   * working, and neither is a security decision: every call is checked again server-side (D18), so
   * this only decides what is OFFERED.
   */
  /* The room read carries a second answer and `mayRunRoom` prefers it — in CORE, where the phone
     reads the same rule. This file merged the two itself for an afternoon, which meant the phone
     did not: the appointment the rule exists for reached one client and not the other. */
  const iHost = mayRunRoom(room, meId, mayModerate);

  /*
   * WHOM THIS ROOM HAS BARRED — asked for only by somebody who can act on the answer.
   *
   * A ban was a one-way door by accident: `bannedIds` is deliberately absent from the room's view,
   * so a host had nobody to name and `unbanFromRoom` — which the server has always answered — was
   * unreachable from anywhere.
   *
   * Re-asked whenever the room changes, because banning somebody is what most often changes it, and
   * a list that still showed them a minute later would be a host wondering whether the ban worked.
   */
  /*
   * AND A FAILED READ IS SAID, because of what the paragraph above establishes: `bannedIds` is
   * deliberately absent from the room's view, so this list is the ONLY place a room's ban is
   * visible and the only path to lifting one. Emptying it on failure drew the answer "nobody is
   * barred" — the state a host is most likely to accept without question — and took the undo away
   * with it. That is the ban going back to being a one-way door, by a different route.
   */
  useEffect(() => {
    if (!iHost) { setBarred([]); setBarredUnread(false); return undefined; }
    let live = true;
    client.roomBanned(room.id)
      .then(found => { if (live) { setBarred(found); setBarredUnread(false); } })
      .catch(() => { if (live) setBarredUnread(true); });
    return () => { live = false; };
  }, [client, room, iHost]);

  /*
   * WHO TO DRAW. A room holds 300 (§5.2) and this drew every one of them, with four buttons each,
   * re-rendered every time the active speaker changed. The cost is the small half; the large half
   * is that rule 2's controls stop being reachable — a host who can HEAR somebody shouting had to
   * scroll a list of hundreds to find them, while the shouting went on.
   *
   * `rosterFor` is in core rather than here so the phone cannot come to a different answer about
   * who matters in a crowded room. It keeps whoever is speaking, the stage, and this reader, and
   * never reorders.
   */
  const { rows: drawn, hidden } = rosterFor(participants, { revealAll: wholeRoom, selfId: meId });

  /* The queue below turns ids into names, and did it with a `find` per row: 300 hands against 300
     participants is ninety thousand comparisons every time anybody starts talking. */
  const byIdentity = new Map(participants.map(participant => [participant.identity, participant]));

  /*
   * ONLY THE HANDS THIS HOST CAN ACT ON.
   *
   * `raisedIds` is the room's list and the roster is who is actually here, and they disagree: a
   * hand raised by somebody who has since gone stays on the room until the SFU's departure event
   * reaches us, and a row nobody can name is skipped. So the heading counted twelve and the list
   * showed three — a leader working a queue, told a number they could not act on.
   *
   * The server now drops a hand when its owner leaves, which fixes the cause. This is the other
   * half: whatever the room says, the count a host reads is the count of people they can promote.
   */
  const asking = room.raisedIds.filter(identity => byIdentity.has(identity));

  /* How full the stage is, or null in a room that has no stage — the rule is in core so the phone
     cannot answer it differently. Read from the ROOM rather than the roster: the cap the server
     enforces is over `speakerIds`, and a count of who is audible right now is a different number. */
  const seats = stageSeats(room);

  /*
   * WHETHER THIS READER IS ACTUALLY AUDIBLE — read off the connection, not remembered from the last
   * time they pressed the button.
   *
   * This was a `useState(true)` that only our own button ever moved, and that is wrong in the exact
   * case the product exists to handle: **a host mutes somebody** (D18). The mute goes client →
   * server → SFU → that reader's browser and arrives as a track-mute, the roster above sees it, and
   * the button went on saying "Mute" — telling a reader they were live while nothing was being
   * sent, which is the same lie as a track that never published. They then had to press twice to
   * come back, because the first press was the state catching up with reality.
   *
   * Found by two browsers in one room (`web/e2e/room-two.spec.ts`) and by nothing else: with one
   * browser and a fake connector there is nobody to do the muting.
   *
   * The roster carries the local participant too, so this needs no second source. Before the first
   * roster arrives — a tick, since the connector pushes on subscribe — assume live, which is what
   * the SDK has just been asked for.
   */
  const micOn = mine ? !mine.muted : true;
  /*
   * THE SAME ANSWER, READABLE AFTER AN AWAIT — a `const` from this render is the state as it was
   * when the press started, and the question below is about the state a moment after it ended.
   */
  const nowOn = useRef(micOn);
  nowOn.current = micOn;

  const toggleMic = async (): Promise<void> => {
    /*
     * THAT THE HANDLER RAN AT ALL — the last fork, and the only one instrumentation outside the
     * app cannot close.
     *
     * Six reds have shown a DOM click landing on this button (a listener the gate attaches sees
     * it) with nothing whatever following: no publish, no throw, no sentence, and — once the
     * panel began saying so after four seconds — not even that. Every one of those is scheduled
     * INSIDE this function, so a React handler that never runs looks exactly like all of them at
     * once. The page clock was measured at the moment of failure and is honest (101ms for 100),
     * so a slowed timer is not the answer.
     *
     * `debug`, not `warn`: browsers hide it unless somebody asks for verbose, and a press is a
     * deliberate act rather than a problem. It can go the day this fork is closed.
     */
    console.debug('[social] microphone press');
    setSaid(null);
    /* The press is the answer to the silence as well: it republishes, and the SDK looks again. */
    setMicSilent(false);
    /* Read once, because the answer decides which sentence a failure gets and the roster may have
       moved under us by the time one arrives. */
    const opening = !micOn;
    /*
     * AND A PRESS THAT NEVER ANSWERS AT ALL — the last way this control could stay silent.
     *
     * The connector promises an answer inside its own budget: it waits up to three seconds for the
     * grant, then publishes or gives up, and giving up throws. So a press that has said nothing
     * after four is outside every promise the layer makes, and the reader is left holding a button
     * that did nothing — which §0 calls the one outcome a live control must never have.
     *
     * A browser gate met exactly that six times: the click witnessed, the SFU publishing, and
     * neither branch of `setMicrophoneEnabled` reached. Whatever is happening in there, the reader
     * is not going to be told by it, so they are told here.
     */
    let answered = false;
    const late = window.setTimeout(() => {
      if (answered) return;
      console.warn(`[social] the microphone press has not answered in four seconds (asked for ${opening ? 'on' : 'off'})`);
      setSaid(t(opening ? 'rooms.micDidNotOpen' : 'rooms.micStillOn'));
    }, 4000);
    try {
      await session.setMicrophoneEnabled(opening);
      answered = true;
      /*
       * AND A PRESS THAT CHANGED NOTHING SAYS SO — the outcome §0 forbids, which the product
       * could still produce in silence.
       *
       * The connector answers this call three ways: it publishes, it throws, or it resolves
       * having done nothing at all. The third leaves a reader with a live button, an unchanged
       * label and **no sentence anywhere** — and a browser gate has now met exactly that six
       * times, most recently with the click itself witnessed and the page otherwise silent.
       *
       * The roster is told the moment the publish resolves (see `tellTheRoster` in the
       * connector), so a second later it either agrees with the press or the press did nothing.
       * A second is long enough for a state that is already in hand and short enough that a
       * reader is still looking at the button they pressed.
       */
      window.setTimeout(() => {
        if (nowOn.current === opening) return;
        console.warn(`[social] the microphone press resolved and changed nothing (asked for ${opening ? 'on' : 'off'})`);
        setSaid(t(opening ? 'rooms.micDidNotOpen' : 'rooms.micStillOn'));
      }, 1000);
    } catch (caught) {
      /*
       * A LIVE BUTTON THAT DID NOTHING IS THE WORST ANSWER HERE — worse than a refusal, because a
       * refusal at least tells somebody where they stand. The connector tries three times before it
       * says this, so by the time a reader sees it the room server really has declined to carry
       * their voice, and the next useful thing is to press again rather than to keep talking to a
       * room that cannot hear them.
       *
       * AND ANYTHING ELSE WAS DROPPED, which is that same sentence happening to this handler.
       *
       * Two named cases were caught and everything else fell out of the bottom in silence — the
       * defect the function below this one has in its own comment: *"this caught only
       * `NotAHostError` and swallowed everything else"*. `setMicrophoneEnabled` reaches
       * `getUserMedia`, which rejects with the browser's own errors and not with ours: a device
       * another program holds (`NotReadableError`), one that has gone away (`NotFoundError`), a
       * permission dialog dismissed rather than answered (`NotAllowedError`). Every one of those
       * left the press with no microphone and no sentence.
       *
       * WHICH WAY THE PRESS WAS GOING DECIDES THE WORDS. Failing to OPEN leaves a reader silent,
       * which they can see; failing to CLOSE leaves them **audible while believing they are not**,
       * and that is the one of the two worth its own sentence.
       *
       * Written down as well as shown, for the reason `refusalWords` gives: a browser gate that
       * catches this again should find a cause in the console rather than an empty array.
       */
      if (caught instanceof MicrophoneDidNotOpenError) setSaid(t('rooms.micDidNotOpen'));
      else if (caught instanceof RefusedError) setSaid(refusalSays(t, caught));
      else {
        console.warn('[social] the microphone press failed in a way this panel does not name:', caught);
        setSaid(t(opening ? 'rooms.micDidNotOpen' : 'rooms.micStillOn'));
      }
    } finally {
      /* However it ended, it ended: the four-second sentence is about silence, not about failure. */
      answered = true;
      window.clearTimeout(late);
    }
  };

  /**
   * Every host control fails the same way, so it is handled once — and there are TWO ways.
   *
   * This caught only `NotAHostError` and swallowed everything else, which meant the one refusal a
   * host can actually act on vanished: "the stage is full (8)". They clicked to promote a ninth
   * speaker and the product did not react. Authority is its own case because it has its own
   * sentence about a panel gone stale; everything else the server explained is shown as written.
   */
  /**
   * A change WE made, applied here. Telling the room is no longer this component's job.
   *
   * It was: every control that returned a room went through here so that nobody had to remember to
   * tell the others. The funnel was right and the place was wrong — a browser can only announce a
   * change IT made, and the changes nobody in the room makes are the ones nothing else would ever
   * correct. A second Hall's leadership accepting a co-host invitation is decided on their own page
   * by somebody who was never a participant, so the host here went on reading "asked" for as long
   * as they stayed. The server announces now, for every route, and is held to it by a test.
   *
   * Kept as a name rather than inlined: what these call sites are doing is applying the answer the
   * server just gave, and `onRoomChanged` alone reads like a callback about somebody else's change.
   */
  const changedByMe = (fresh: RoomView): void => {
    onRoomChanged(fresh);
  };

  /*
   * ONE END-CO-HOSTING PER ROOM. `DELETE rooms/:id/cohost` refuses with *"only one of the two
   * halls' leadership can end that"* once the arrangement is gone — so a host who presses twice is
   * told they are not the leadership they are, which is a claim about their standing rather than
   * about a request that failed. The Hall page has the same control and the same fix.
   *
   * Nothing else in this panel needs it. Raising a hand is `$addToSet`, and silencing, sharing
   * hostship and putting somebody on the stage refuse only a non-host — so repeating any of them
   * answers what the first press did. See `useOneAtATime` for the test that decides which is which.
   */
  const row = useOneAtATime();

  /** End a co-hosting arrangement — one per room, and `asHost` says whatever comes back. */
  const endCoHosting = async (): Promise<void> =>
    row.run(room.id, () => asHost(async () => { changedByMe(await client.endCoHost(room.id)); }));

  const asHost = async (action: () => Promise<void>): Promise<void> => {
    setRefused(false);
    setSaid(null);
    try {
      await action();
    } catch (caught) {
      if (caught instanceof NotAHostError) setRefused(true);
      /*
       * AN EXPIRED SESSION FELL OUT OF THE BOTTOM OF THIS, in the panel whose own comment eighty
       * lines up calls that the worst answer available: *"a live button that did nothing is the
       * worst answer here — worse than a refusal, because a refusal at least tells somebody where
       * they stand."*
       *
       * `SocialAuthError extends Error`, not `RefusedError`, so it matched neither branch and there
       * was no third. A host on day six of a seven-day session pressed Silence in a room that was
       * going wrong and **nothing happened at all** — no sentence, no refusal, no sign-in, for ever.
       * The comment on the line below made the omission invisible: it said *only what the server
       * deliberately wrote*, and a 401 IS that.
       *
       * The words say what to do rather than only what failed, because reloading really does offer
       * the way back: `me()` answers null for a refused session — throwing only for everything
       * else — so `App` clears the reader and the account bar becomes a sign-in again.
       */
      else if (caught instanceof SocialAuthError) setSaid(t('auth.sessionEnded'));
      /* Only what the server deliberately WROTE — a dropped connection is an Error too. */
      else if (caught instanceof RefusedError) setSaid(refusalSays(t, caught));
      /*
       * AND EVERYTHING ELSE, which the line above used to be the reason for dropping.
       *
       * That reason was half of one: the server's own sentence is not the right thing to show for a
       * dropped connection, and NOTHING is not the right thing either. The handler eighty lines up
       * settled this for itself and said so — *"and anything else was dropped, which is that same
       * sentence happening to this handler… every one of those left the press with no microphone and
       * no sentence"* — and it names THIS function as where it learnt it. It was still the laggard.
       *
       * A host on bad wifi pressing Close on a room that is going wrong is the case: the request
       * never lands, and a control that answers nothing tells them to press it again for ever.
       * `rooms.wentWrong` is the phrase the rooms panel already uses for exactly this and says the
       * one thing worth saying — nothing is lost.
       */
      else setSaid(t('rooms.wentWrong'));
    }
  };

  return (
    <section className="room" aria-label={t('voice.label')}>
      <h3 className="panel-heading">{title}</h3>

      {/*
        THE BROWSER IS NOT PLAYING THE ROOM — one control, whose label is the whole sentence.

        A separate line explaining it and a button to fix it would be two things to read for a
        state that needs one gesture; the press IS the gesture the browser is holding out for, so
        the sentence and the target are the same object. First in the room, above everything,
        because there is nothing to read below it that is worth reading unheard.
      */}
      {deaf && session.startPlayback && (
        <button
          type="button"
          className="room-deaf"
          onClick={() => void session.startPlayback?.().catch((caught: unknown) => {
            /* Said rather than swallowed, for the reason the microphone press now gives: a press
               that neither works nor speaks is the worst answer this panel can give. The state
               stays blocked, so the control stays there to try again. */
            console.warn('[social] the browser would not start playing the room:', caught);
          })}
        >
          {t('rooms.notPlaying')}
        </button>
      )}

      {/* The gap, named. A reader whose train went into a tunnel wants to know why the room went
          quiet — not to be told nothing, and not to be thrown out while the SDK is still trying. */}
      {connection === 'reconnecting' && (
        <p className="room-reconnecting" role="status" aria-live="polite">{t('rooms.reconnecting')}</p>
      )}

      {/*
        THE CODE, TO THE HOST, ONCE.
        An invite room is a door nobody can see (D24), so the code is the entire way in — and the
        server sends it back exactly once, in the response to the host who created the room. Shown
        here because this is where they are standing the moment after they make it, and a secret
        they were handed and never shown is a room that can only ever hold its owner.
      */}
      {iHost && room.inviteCode && (
        <p className="room-invite">
          {t('rooms.shareInvite')}
          <code className="room-invite-code">{room.inviteCode}</code>
        </p>
      )}

      <div className="room-self">
        {/*
          OFFERED ONLY WHEN IT CAN DO SOMETHING.

          A reader who may not publish — a listener in a room on the stage, or somebody a leader has
          just given the floor whose grant is still in flight — could press this and nothing at all
          happened: the connector asks the SFU whether we may publish and returns quietly when the
          answer is no. A control that silently does nothing is worse than one that is visibly
          unavailable, and it is exactly what a reader meets in the second after being let up, which
          is the moment they are most likely to press it.

          Read from the roster, so it turns itself on the instant the grant lands.
        */}
        <button
          type="button"
          className="room-mic"
          onClick={() => void toggleMic()}
          aria-pressed={!micOn}
          disabled={mine ? !mine.canSpeak : false}
        >
          {micOn ? t('rooms.muteSelf') : t('rooms.unmuteSelf')}
        </button>
        {/*
          FOLLOWING, AND STOPPING — §11 says this is opt-out, per reader, and stays opt-out.

          Being dragged around a text you are reading is hostile, and worst for whoever joined
          late and is still finding their place. So the control is here rather than in a menu,
          it says which state it is in rather than what pressing it does, and re-joining is the
          same press. Drawn only where a move can actually arrive: a session that cannot carry
          one, or an app with no view to open, would be offering a switch over nothing.

          NOT BUILT YET, and named so it is not mistaken for done: §11 also asks that a reader
          who navigates away STOPS following without pressing anything. That needs the app to
          tell the room a move was the reader's own, which is the sending half.
        */}
        {session.onFollow && onFollowed && view && (
          <button
            type="button"
            className={following ? 'room-following' : 'room-following off'}
            aria-pressed={following}
            onClick={() => setFollowing(now => !now)}
          >
            {following ? t('rooms.following') : t('rooms.followAgain')}
          </button>
        )}
        {/*
          OPEN AND SENDING NOTHING — beside the button, because that is what a reader presses next.

          Only while the microphone is actually open: a listener has nothing to be told, and a
          reader who has muted themselves has already answered the question.
        */}
        {micSilent && micOn && (
          <p className="room-silent" role="status">{t('rooms.micSilent')}</p>
        )}
        {/*
          ASK TO SPEAK (D22) — for somebody who is NOT on the stage and does not host the room,
          which is nearly everyone in a room of three hundred.

          The stage and promotion were built; the way to ask was not, so a listener with a question
          could only wait to be noticed by whoever was addressing the room. Absent under `open`
          policy, where everybody already holds a microphone and asking for one would be a control
          that does nothing.
        */}
        {!iHost && room.policy === 'stage' && meId !== null && !room.speakerIds.includes(meId) && (
          <button
            type="button"
            className={room.raisedIds.includes(meId) ? 'room-hand raised' : 'room-hand'}
            aria-pressed={room.raisedIds.includes(meId)}
            onClick={() => void asHost(async () => {
              changedByMe(room.raisedIds.includes(meId)
                ? await client.lowerHand(room.id, meId)
                : await client.raiseHand(room.id));
            })}
          >
            {room.raisedIds.includes(meId) ? t('rooms.handDown') : t('rooms.handUp')}
          </button>
        )}
        {/* A deliberate leave carries no reason — the reader knows why they left. */}
        <button type="button" className="room-leave" onClick={() => onLeave()}>{t('voice.leave')}</button>
        {/*
          SILENCE THE ROOM. Every control on a participant below is per-person, which is right for
          "your dog is barking" and useless for a leader in front of three hundred: muting a room
          one row at a time is a race, not moderation.

          It takes the grant rather than muting the track, so the quiet holds — and it puts the room
          on the stage, so the next person to arrive is a listener too. Handing the microphone back
          is the "to the stage" control that already exists, one person at a time, which is the
          shape a taught session actually has.
        */}
        {iHost && (
          <button
            type="button"
            className="room-silence"
            onClick={() => void asHost(async () => { changedByMe(await client.silenceRoom(room.id)); })}
          >
            {t('rooms.silence')}
          </button>
        )}
        {/*
          THE DOOR, MID-CONVERSATION (D24) — and the answer to the bad day this layer is most
          likely to have.

          A public room being raided could only be defended one arrival at a time, with ban and
          remove, for as long as people kept arriving. This shuts the door, and the arriving stops.
          Nobody already inside is touched: they are what ban and remove are for, and emptying a
          study to save it would be the raid winning.

          ONE TAP, AND IT LOCKS TO INVITE, rather than asking which kind of door. In the moment this
          control exists for there is no time to choose, and the mode that needs no typing is the
          one that can be reached in a hurry. It also mints a fresh code every time it is pressed,
          which is the same act as rotating a code that ended up somewhere public — the code shown
          above updates itself, because the reply carries it.
        */}
        {iHost && doorControls(room).map(control => (
          <button
            key={control.kind}
            type="button"
            className={DOOR_BUTTONS[control.kind].className}
            onClick={() => void asHost(async () => { changedByMe(await client.setRoomAccess(room.id, control.to)); })}
          >
            {t(DOOR_BUTTONS[control.kind].label)}
          </button>
        ))}
        {iHost && (
          <button
            type="button"
            className="room-close"
            onClick={() => void asHost(async () => { changedByMe(await client.closeRoom(room.id)); onLeave(); })}
          >
            {t('rooms.close')}
          </button>
        )}
      </div>

      {/*
        THE QUEUE, FOR A HOST — in the order people asked, which is the whole point of it.
        `raisedIds` is an array and the server adds with `$addToSet`, so tapping twice cannot move
        anybody up: asking first means being asked first.

        Names come from the ROSTER, because the room view carries ids and D10 keeps names out of it.
        Somebody who raised a hand and then left is not in the roster, and is skipped rather than
        rendered as an id — a host cannot act on a person who is not here.

        Absent when nobody is asking, rather than an empty heading: for most of a room's life the
        queue is empty, and a section that usually says nothing is furniture.
      */}
      {iHost && asking.length > 0 && (
        <>
          <h4 className="room-hands-heading">{t('rooms.asking', { count: asking.length })}</h4>
          <ol className="room-hands">
            {asking.slice(0, QUEUE_ROWS).map(identity => {
              const who = byIdentity.get(identity);
              if (!who) return null;
              return (
                <li key={identity} className="room-hand-row">
                  <span className="room-hand-who">{who.name}</span>
                  <button
                    type="button"
                    className="room-hand-let"
                    onClick={() => void asHost(async () => { changedByMe(await client.setOnStage(room.id, identity, true)); })}
                  >
                    {t('rooms.speakNext', { name: who.name })}
                  </button>
                  {/* Not a rejection with a sentence attached — it is how a queue gets worked
                      through when the answer is "not this time". */}
                  <button
                    type="button"
                    className="room-hand-pass"
                    onClick={() => void asHost(async () => { changedByMe(await client.lowerHand(room.id, identity)); })}
                  >
                    {t('rooms.notNow', { name: who.name })}
                  </button>
                </li>
              );
            })}
          </ol>
          {/*
            THE REST OF THE QUEUE IS A NUMBER, and deliberately not a list.

            Unlike the roster there is nothing to open: the queue's order IS its fairness rule, a
            host works the front of it, and a control that let one be picked out of three hundred
            would be a way to jump the line rather than a way to see it. The count is what a host
            needs — it tells them whether to keep taking questions or to say there is no time.
          */}
          {asking.length > QUEUE_ROWS && (
            <p className="room-hands-more">
              {t('rooms.moreAsking', { count: String(asking.length - QUEUE_ROWS) })}
            </p>
          )}
        </>
      )}

      {/*
        ASK ANOTHER COMMUNITY TO CO-HOST THIS (COMMUNITY.md D22) — for a host, and only for a room
        that is already under a Hall: co-hosting is two communities, so there has to be a first one.

        BY ADDRESS, because that is how one leader names another. A Hall's address is the thing they
        put in a YouTube description; there is no directory of communities to pick from and there
        should not be — D21 refuses a platform-wide graph, and a picker of every Hall in the product
        would be one.

        The state after asking says "asked", not "co-hosting". Until they accept, this room lists
        where it always did and their leadership moderates nothing — see `coHall`.
      */}
      {iHost && room.hallId && (
        <div className="room-cohost">
          {room.coHall ? (
            <p className="room-cohost-state">
              {room.coHall.acceptedAt ? t('rooms.coHosting') : t('rooms.coHostAsked')}
              <button
                type="button"
                className="room-cohost-end"
                onClick={() => void endCoHosting()}
                disabled={row.working(room.id)}
              >
                {t('rooms.coHostEnd')}
              </button>
            </p>
          ) : (
            <form
              className="room-cohost-ask"
              onSubmit={event => {
                event.preventDefault();
                const wanted = coHostSlug.trim().toLowerCase();
                if (!wanted) return;
                void asHost(async () => {
                  const hall = await client.hall(wanted);
                  /* A refusal the reader can act on: the address is theirs to check, and "no such
                     community" is the only thing we know. */
                  if (!hall) { setSaid(t('rooms.coHostNoSuchHall')); return; }
                  changedByMe(await client.proposeCoHost(room.id, hall.id));
                  setCoHostSlug('');
                });
              }}
            >
              <label className="sr-only" htmlFor={`cohost-${room.id}`}>{t('rooms.coHostAsk')}</label>
              <input
                id={`cohost-${room.id}`}
                className="room-cohost-input"
                value={coHostSlug}
                onChange={event => setCoHostSlug(event.target.value)}
                placeholder={t('rooms.coHostPlaceholder')}
              />
              <button type="submit" className="room-cohost-go" disabled={!coHostSlug.trim()}>
                {t('rooms.coHostAsk')}
              </button>
            </form>
          )}
        </div>
      )}

      <h4 className="room-participants-heading">{t('rooms.participants')}</h4>

      {/*
        HOW FULL THE STAGE IS — the ceiling, before somebody hits it.
        `stageLimit` is sent in every room view and was read by nothing. A host worked a queue of
        raised hands, promoted, and learned the number from the REFUSAL: "the stage is full (8)",
        a moment after knowing would have let them take somebody off first. A listener deciding
        whether to bother asking could not see whether there was anywhere to be promoted to.

        One line, and only where there is a stage: `stageSeats` returns null under `open` policy,
        because a seat count in a room where everyone may speak is a number about a mechanism that
        is not running — which reads as a limit somebody is subject to.
      */}
      {seats && (
        <p className="room-stage-seats">
          {t('rooms.stageSeats', { taken: String(seats.taken), limit: String(seats.limit) })}
        </p>
      )}

      <ul className="room-participants">
        {drawn.map(participant => {
          const isMe = participant.identity === meId;
          const theyHost = room.hostIds.includes(participant.identity);
          return (
            <li
              key={participant.identity}
              className={participant.speaking ? 'room-participant speaking' : 'room-participant'}
            >
              <span className="room-participant-name">{participant.name}</span>
              {theyHost && <span className="room-badge">{t('rooms.hostBadge')}</span>}
              {!participant.canSpeak && <span className="room-badge">{t('rooms.listening')}</span>}
              {/*
                THEIR AUDIO NEVER REACHED THIS BROWSER — on the row it is about, and nowhere else.

                A subscription can fail for one listener and nobody else, and when it does the SFU
                goes on reporting that person as speaking, so this row ANIMATES while the reader
                hears nothing. A banner would be the wrong shape: what is wrong is about one
                person, and this is where their name already is.
              */}
              {participant.unheard && <span className="room-badge unheard">{t('rooms.unheard')}</span>}

              {/*
                SILENCE THEM FOR YOURSELF — offered to EVERYONE, not only a host, and offered on a
                host too. It is not a moderation act: nothing is sent anywhere, nobody is told, and
                the room is unchanged, so there is no authority to check and nobody to protect from
                it. That is exactly why it belongs to every reader.

                Only for somebody who can actually speak, and never for yourself: the microphone
                button above is what silences you, and a control that turned your own playback down
                would do nothing a reader could perceive.
              */}
              {session.setParticipantMuted && !isMe && participant.canSpeak && (
                <button
                  type="button"
                  className={hushed.includes(participant.identity) ? 'room-hush hushed' : 'room-hush'}
                  aria-pressed={hushed.includes(participant.identity)}
                  onClick={() => {
                    const now = !hushed.includes(participant.identity);
                    session.setParticipantMuted?.(participant.identity, now);
                    setHushed(current => (now
                      ? [...current, participant.identity]
                      : current.filter(id => id !== participant.identity)));
                  }}
                >
                  {hushed.includes(participant.identity)
                    ? t('rooms.hearAgain', { name: participant.name })
                    : t('rooms.hushForMe', { name: participant.name })}
                </button>
              )}

              {iHost && !isMe && (
                <span className="room-controls">
                  {participant.canSpeak && (
                    <button
                      type="button"
                      onClick={() => void asHost(() => client.muteParticipant(room.id, participant.identity))}
                    >
                      {t('rooms.forceMute', { name: participant.name })}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void asHost(async () => {
                      changedByMe(await client.setOnStage(room.id, participant.identity, !participant.canSpeak));
                    })}
                  >
                    {participant.canSpeak
                      ? t('rooms.offStage', { name: participant.name })
                      : t('rooms.toStage', { name: participant.name })}
                  </button>
                  <button
                    type="button"
                    onClick={() => void asHost(() => client.removeParticipant(room.id, participant.identity))}
                  >
                    {t('rooms.kick', { name: participant.name })}
                  </button>
                  <button
                    type="button"
                    onClick={() => void asHost(async () => {
                      changedByMe(await client.banFromRoom(room.id, participant.identity));
                    })}
                  >
                    {t('rooms.ban', { name: participant.name })}
                  </button>

                  {/*
                    SHARE THE ROOM, which no renderer offered.

                    A commons room has exactly ONE moderator — whoever opened it — and a room holds
                    up to 300 (§5.2). One person cannot watch three hundred, which is the whole
                    reason D22 gives a Hall a mod team; a room outside a Hall had no equivalent, and
                    if its only host left, nobody could mute or remove anyone for the rest of its
                    life. `addHost` was answered by the server, tested, and called by nothing.

                    Offered only for somebody who is not already a host, and there is deliberately
                    no way to take it back: hostship is shared with a person you trust, and a room
                    where hosts can demote each other is a room with a coup in it. A room ends when
                    it empties (D23), so the grant cannot outlive the conversation.
                  */}
                  {!theyHost && (
                    <button
                      type="button"
                      className="room-share-host"
                      onClick={() => void asHost(async () => {
                        changedByMe(await client.addHost(room.id, participant.identity));
                      })}
                    >
                      {t('rooms.makeHost', { name: participant.name })}
                    </button>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {/*
        THE REST OF THE ROOM, as a number and one control.

        Not load-on-scroll, which is this product's usual answer and is the wrong one here: rows
        appearing above the cursor while a host is reaching for "remove" is how the wrong person
        gets removed. A reader who wants the whole list asks once and keeps it.
      */}
      {hidden > 0 && (
        <button type="button" className="room-show-all" onClick={() => setWholeRoom(true)}>
          {t('rooms.showEveryone', { count: String(hidden) })}
        </button>
      )}

      {/*
        WHOM THIS ROOM HAS BARRED, to whoever can lift it — and absent for everybody else, which is
        most rooms most of the time. A section that usually says "nobody" is furniture, and this one
        would be furniture that names people.

        The whole point is the button beside each name: `unbanFromRoom` was answered by the server
        and reachable from nowhere, because there was no way to see who was on the list. A ban was
        a decision that could never be revisited, which is not what anybody chose.
      */}
      {iHost && (barred.length > 0 || barredUnread) && (
        <>
          <h4 className="room-barred-heading">{t('rooms.barredHeading')}</h4>
          {barredUnread && (
            <p className="room-refused" role="alert">{t('shared.couldNotRead')}</p>
          )}
          <ul className="room-barred">
            {barred.map(person => (
              <li key={person.userId} className="room-barred-row">
                {/* Somebody who has deleted their account still holds a place on the list, and a
                    host may reasonably want to clear it — so the row works without a name. */}
                <span className="room-barred-who">{person.displayName ?? t('rooms.departedReader')}</span>
                <button
                  type="button"
                  className="room-unban"
                  onClick={() => void asHost(async () => {
                    changedByMe(await client.unbanFromRoom(room.id, person.userId));
                  })}
                >
                  {t('rooms.letBackIn', { name: person.displayName ?? t('rooms.departedReader') })}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {refused && <p className="room-refused" role="alert">{t('rooms.notHost')}</p>}
      {/* The server's own words — "the stage is full (8)" names the limit, which no phrase here
          could without keeping a second copy of a number the server owns. */}
      {said && <p className="room-refused" role="alert">{said}</p>}

      {/* Live text rides the same connection as the audio (D12) — no extra infrastructure. The
          hush goes with it: "silence them for me" that left their typing in front of the reader was
          only silencing half of somebody. */}
      <RoomChat chat={session} meId={meId} hushed={hushed} />
    </section>
  );
}
